import {
  vCountryAny,
  vDateUtcNoon,
  vEmailOrEmpty,
  vMetadata,
  vNumber,
  vObject,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPhoneExt,
  vPhoneNumber,
  vPicklist,
  vPipe,
  vPostalCode,
  vString,
} from '../../validation/index.ts'
import { customsBrokerTypes } from '../customs-broker.ts'
import {
  defaultFlagSchema,
  type EntriesWithSuffix,
  schedulePickupSchema,
  selectQuoteSchema,
  shipTypeSchema,
  strictCloseDockHoursSchema,
  strictOpenDockHoursSchema,
} from './common.ts'
import { flatItemSchema } from './flat-item.ts'

const getItemSchema = <Suffix extends string>(suffix: Suffix) => {
  return vObject<EntriesWithSuffix<typeof flatItemSchema.entries, Suffix>>(
    Object.entries(flatItemSchema.entries).reduce((acc, [key, value]) => {
      acc[`${key}${suffix}`] = value
      return acc
    }, {} as any),
  )
}

export const ltlFlatLoadSchema = vObject({
  ...schedulePickupSchema.entries,
  pickupDate: vPipe(vDateUtcNoon(), vMetadata({ minWidth: 150 })),
  bolNumber: vOptional(vString()),
  ...selectQuoteSchema.entries,
  originCompany: vOptional(vString()),
  originAddress: vOptional(vString()),
  originAddress2: vOptional(vString()),
  originCity: vOptional(vString()),
  originState: vOptional(vString()),
  originPostalCode: vPostalCode(),
  originCountry: vOptionalDef(vCountryAny, 'us'),
  originType: shipTypeSchema,
  originContactName: vOptional(vString()),
  originContactPhone: vOptional(vPhoneNumber()),
  originContactPhoneExt: vOptional(vPhoneExt()),
  originContactEmail: vOptional(vEmailOrEmpty),
  originReferenceNumber: vOptional(vString()),
  originInstructions: vOptional(vString()),
  originDockHoursOpen: strictOpenDockHoursSchema,
  originDockHoursClose: strictCloseDockHoursSchema,
  destCompany: vOptional(vString()),
  destAddress: vOptional(vString()),
  destAddress2: vOptional(vString()),
  destCity: vOptional(vString()),
  destState: vOptional(vString()),
  destPostalCode: vPostalCode(),
  destCountry: vOptionalDef(vCountryAny, 'us'),
  destType: shipTypeSchema,
  destContactName: vOptional(vString()),
  destContactPhone: vOptional(vPhoneNumber()),
  destContactPhoneExt: vOptional(vPhoneExt()),
  destContactEmail: vOptional(vEmailOrEmpty),
  destReferenceNumber: vOptional(vString()),
  destInstructions: vOptional(vString()),
  destDockHoursOpen: strictOpenDockHoursSchema,
  destDockHoursClose: strictCloseDockHoursSchema,
  billToCompany: vOptional(vString()),
  billToAddress: vOptional(vString()),
  billToAddress2: vOptional(vString()),
  billToCity: vOptional(vString()),
  billToState: vOptional(vString()),
  billToPostalCode: vOptional(vPostalCode()),
  billToCountry: vOptional(vCountryAny),
  billToContactName: vOptional(vString()),
  billToContactPhone: vOptional(vPhoneNumber()),
  billToContactPhoneExt: vOptional(vPhoneExt()),
  billToContactEmail: vOptional(vEmailOrEmpty),
  customsBrokerCompanyName: vOptional(vString()),
  customsBrokerAddress: vOptional(vString()),
  customsBrokerAddress2: vOptional(vString()),
  customsBrokerCity: vOptional(vString()),
  customsBrokerState: vOptional(vString()),
  customsBrokerPostalCode: vOptional(vPostalCode(true)),
  customsBrokerCountry: vOptional(vCountryAny),
  customsBrokerPhone: vOptional(vPhoneNumber()),
  customsBrokerPhoneExt: vOptional(vPhoneExt()),
  customsBrokerEmail: vOptional(vEmailOrEmpty),
  customsBrokerDeclaredValue: vOptional(vNumber()),
  customsBrokerType: vOptional(vPicklist(customsBrokerTypes)),
  emergencyName: vOptional(vString()),
  emergencyPhone: vOptional(vPhoneNumber()),
  emergencyPhoneExt: vOptional(vPhoneExt()),
  arrivalNoticeFlag: defaultFlagSchema,
  arrivalScheduleFlag: defaultFlagSchema,
  liftgatePickupFlag: defaultFlagSchema,
  liftgateDeliveryFlag: defaultFlagSchema,
  insidePickupFlag: defaultFlagSchema,
  insideDeliveryFlag: defaultFlagSchema,
  sortAndSegregateFlag: defaultFlagSchema,
  protectFromFreezingFlag: defaultFlagSchema,
  singleShipmentFlag: defaultFlagSchema,
  ...getItemSchema('1').entries,
  ...getItemSchema('2').entries,
  ...getItemSchema('3').entries,
  ...getItemSchema('4').entries,
  ...getItemSchema('5').entries,
  ...getItemSchema('6').entries,
  ...getItemSchema('7').entries,
  ...getItemSchema('8').entries,
  ...getItemSchema('9').entries,
  ...getItemSchema('10').entries,
})

export type LtlFlatLoad = vOutput<typeof ltlFlatLoadSchema>
